/**
 * mobile-scope.ts — Chức năng nào dùng được trên ĐIỆN THOẠI (< 768px). Bản phone tập trung vào việc lõi hằng ngày:
 * trả lời khách (Hộp thư), tra cứu khách hàng, xem nhanh Tổng quan, thông báo, tài khoản & gói dịch vụ. 20/10/2026
 * mở thêm Kết nối, Cài đặt, Chiến dịch kết bạn Zalo, Gửi tin nhắn Zalo hàng loạt, Nhân viên — phần lớn trang đã có sẵn
 * bố cục `grid-cols-1`/responsive, chỉ cần mở đường dẫn; riêng bảng tài khoản kênh (`accounts-table.tsx`, dùng chung
 * cho mọi trang kênh + Nhân viên) có cột "Thao tác" GHIM PHẢI che kín màn hẹp — đã sửa thành chỉ ghim từ `md:` trở lên.
 *
 * Mọi thứ còn lại là "chỉ dùng trên máy tính" — cấu hình nhiều bước, bảng rộng, canvas kéo thả, nhập / xuất hàng loạt.
 * Một danh sách duy nhất ở đây điều khiển cả menu (thanh tab, hộp Thêm) lẫn việc chặn đường dẫn gõ / lưu thẳng, nên
 * thêm trang mới chỉ cần quyết định một chỗ.
 */
import type { NavItem } from '@/components/shared/nav-config'
import { i18n } from '@/lib/i18n'

export interface DesktopOnlyArea {
  /** Tiền tố đường dẫn (khớp theo đoạn: `/ai` khớp `/ai/train/x`, KHÔNG khớp `/airline`). */
  prefix: string
  /** Tên hiển thị trong thông báo. */
  label: string
}

export const DESKTOP_ONLY_AREAS: DesktopOnlyArea[] = [
  // Khách hàng: chỉ giữ danh sách + hồ sơ từng khách; thao tác hàng loạt / dọn dữ liệu để máy tính.
  { prefix: '/customers/import', get label() { return i18n.t('sharedMisc:mobileScope.importContacts') } },
  { prefix: '/customers/duplicates', get label() { return i18n.t('sharedMisc:mobileScope.duplicateContacts') } },
  { prefix: '/customers/extract-phones', get label() { return i18n.t('sharedMisc:mobileScope.extractPhones') } },
  { prefix: '/customers/segments', get label() { return i18n.t('sharedMisc:mobileScope.segments') } },
  { prefix: '/cdp', get label() { return i18n.t('sharedMisc:mobileScope.segments') } },
  // Tiếp thị, tự động hoá, phân tích sâu
  { prefix: '/zns-campaigns', get label() { return i18n.t('sharedMisc:mobileScope.znsCampaigns') } },
  { prefix: '/automation', get label() { return i18n.t('sharedMisc:mobileScope.automation') } },
  { prefix: '/analytics', get label() { return i18n.t('sharedMisc:mobileScope.analytics') } },
  // Tri thức / sản phẩm / AI — quản trị nội dung dài
  { prefix: '/ai', get label() { return i18n.t('sharedMisc:mobileScope.aiStaff') } },
  { prefix: '/affiliate', get label() { return i18n.t('sharedMisc:mobileScope.affiliate') } },
]

/** Khớp theo ĐOẠN đường dẫn: `/ai` ⊇ `/ai` và `/ai/x`, không phải `/airline`. */
const under = (path: string, prefix: string) => path === prefix || path.startsWith(`${prefix}/`)

/** Vùng chức năng chỉ dùng trên máy tính chứa `pathname`; undefined = dùng được trên điện thoại. */
export function desktopOnlyFor(pathname: string): DesktopOnlyArea | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  return DESKTOP_ONLY_AREAS.find((a) => under(path, a.prefix))
}

/**
 * Menu chính đã lọc cho điện thoại: bỏ mục / trang con chỉ dùng trên máy tính. Mục còn trang con thì đích bấm vào
 * là trang con đầu tiên còn lại; hết trang con thì bỏ cả mục.
 */
export function mobileNav(nav: NavItem[]): NavItem[] {
  const out: NavItem[] = []
  for (const item of nav) {
    if (!item.children) {
      if (!desktopOnlyFor(item.to)) out.push(item)
      continue
    }
    const children = item.children.flatMap((c) => {
      if (desktopOnlyFor(c.to)) return []
      if (!c.children) return [c]
      // Trang có menu con cấp 3: lọc con; hết con dùng được thì bỏ luôn trang.
      const sub = c.children.filter((g) => !desktopOnlyFor(g.to))
      return sub.length ? [{ ...c, children: sub }] : []
    })
    if (children.length === 0) continue
    out.push({ ...item, to: children[0].to, children })
  }
  return out
}
